import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Building2, Compass, Loader2 } from 'lucide-react'

import { FieldLabel } from '@/components/FieldLabel'
import { ObjectPicker } from '@/components/ObjectPicker'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { MultiSelect } from '@/components/ui/multi-select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useNetwork } from '@/contexts/NetworkContext'
import { SitesApi } from '@/services/sites/sitesApi'

export interface SiteRef {
  id: string
  name: string
}

interface TeamSitesCardProps {
  /** Shown when the team's type can be given sites to deliver on. */
  members?: {
    value: SiteRef[]
    onChange: (sites: SiteRef[]) => void
  }
  /** Shown for marketplace publisher teams, which own sites. */
  ownership?: {
    value: string[]
    onChange: (siteIds: string[]) => void
    /** The sites that could be owned: the team's own plus unowned ones it is in. */
    candidates: SiteRef[]
  }
  /** The site groups the team owns, listed read-only. */
  siteGroups: SiteRef[]
}

interface PublisherSites {
  id: string
  name: string
  sites: SiteRef[]
}

/**
 * Picks a publisher and adds every one of its active sites the team is not
 * already a member of. Publishers with nothing left to add are not offered.
 */
function AddPublisherSites({
  value,
  onChange,
}: {
  value: SiteRef[]
  onChange: (sites: SiteRef[]) => void
}) {
  const { t } = useTranslation()
  const { currentNetwork, scopeKey } = useNetwork()
  const [open, setOpen] = useState(false)
  const context = currentNetwork?.marketplacePlatform ? 'marketplace' : undefined

  const { data: sites = [], isFetching } = useQuery({
    queryKey: ['team-publisher-sites', scopeKey],
    queryFn: () => SitesApi.getAll(undefined, undefined, 'ACTIVE'),
    enabled: open,
    staleTime: 60 * 1000,
  })

  const memberIds = new Set(value.map((site) => site.id))
  const byPublisher = new Map<string, PublisherSites>()
  for (const site of sites) {
    if (!site.earningsAccountId || memberIds.has(site.id)) continue
    const publisher = byPublisher.get(site.earningsAccountId) ?? {
      id: site.earningsAccountId,
      name: site.earningsAccountName || site.earningsAccountId,
      sites: [],
    }
    publisher.sites.push({ id: site.id, name: site.name })
    byPublisher.set(publisher.id, publisher)
  }
  const publishers = [...byPublisher.values()].sort((a, b) => a.name.localeCompare(b.name))

  const add = (publisher: PublisherSites) => {
    onChange([...value, ...publisher.sites])
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Building2 className="size-4" />
          {t('teamSites.addFromPublisher', { context })}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <Command>
          <CommandInput placeholder={t('teamSites.searchPublishers', { context })} />
          <CommandList>
            {isFetching && !sites.length ? (
              <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                {t('common.loading')}
              </div>
            ) : (
              <CommandEmpty>{t('teamSites.noPublishers', { context })}</CommandEmpty>
            )}
            {publishers.map((publisher) => (
              <CommandItem
                key={publisher.id}
                value={`${publisher.name} ${publisher.id}`}
                onSelect={() => add(publisher)}
              >
                <span className="truncate">{publisher.name}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {t('teamSites.siteCount', { count: publisher.sites.length })}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** Which sites a team delivers on, owns, and which site groups it owns. */
export function TeamSitesCard({ members, ownership, siteGroups }: TeamSitesCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <Card tone="delivery">
      <CardHeader>
        <CardTitle>
          <CardIcon icon={Compass} />
          {t('teamSites.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="field-grid">
          {members && (
            <div className="p-field full">
              <FieldLabel tooltip={t('teamSites.membersHelp')}>
                {t('teamSites.members', { count: members.value.length })}
              </FieldLabel>
              <ObjectPicker
                queryKey="team-site-picker"
                search={async (term) =>
                  (await SitesApi.getPage({ search: term, pageSize: 50, status: 'ACTIVE' })).results
                }
                selected={members.value}
                onChange={(sites) => members.onChange(sites.map(({ id, name }) => ({ id, name })))}
                addLabel={t('teamSites.addSites')}
                actions={<AddPublisherSites value={members.value} onChange={members.onChange} />}
                searchPlaceholder={t('teamSites.searchSites')}
                emptyText={t('teamSites.noMembers')}
              />
            </div>
          )}

          {ownership && (
            <div className="p-field full">
              <FieldLabel tooltip={t('teamSites.ownedHelp')}>
                {t('teamSites.owned', { count: ownership.value.length })}
              </FieldLabel>
              {ownership.candidates.length ? (
                <MultiSelect
                  options={ownership.candidates.map((s) => ({ value: s.id, label: s.name }))}
                  value={ownership.value}
                  onChange={ownership.onChange}
                  placeholder={t('teamSites.selectOwned')}
                />
              ) : (
                <p className="text-xs text-muted-foreground">{t('teamSites.noOwnable')}</p>
              )}
            </div>
          )}

          {siteGroups.length > 0 && (
            <div className="p-field full">
              <FieldLabel>{t('teamSites.siteGroups', { count: siteGroups.length })}</FieldLabel>
              <ul className="flex flex-col gap-1">
                {siteGroups.map((group) => (
                  <li key={group.id}>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0"
                      onClick={() => navigate(`/publishing/site-groups/${group.id}`)}
                    >
                      {group.name}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
